/**
 * /influencers — discover-score-outreach IG creator pipeline.
 *
 * MVP today: paste a handle (or several, one per line) → enrich edge function
 * pulls profile + last 12 posts via SM IGPD2, AI-scores fit (0-100), themes,
 * cost band, flags → upserts into marketing.influencers. Table is sortable
 * by fit / followers / engagement; status updates are inline.
 *
 * Deferred (separate commits): hashtag-mining discovery, kanban view,
 * email/IG outreach composer, attribution via FLAX-<HANDLE> codes.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Filter,
  Heart,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MotionCard } from '@/components/motion/MotionCard';
import { EmptyState } from '@/components/charts/EmptyState';
import { formatNumberCompact } from '@/lib/format';
import { imgProxy } from '@/lib/img';
import { marketing } from '@/lib/supabase/marketing';
import { cn } from '@/lib/utils';

interface InfluencerRow {
  id: string;
  ig_handle: string;
  display_name: string | null;
  bio: string | null;
  profile_picture_url: string | null;
  website: string | null;
  followers: number | null;
  posts_total: number | null;
  posts_per_week: number | null;
  reel_pct: number | null;
  avg_likes: number | null;
  avg_comments: number | null;
  avg_views: number | null;
  engagement_rate_pct: number | null;
  themes: string[] | null;
  brand_fit_score: number | null;
  fit_rationale: string | null;
  est_cost_band: string | null;
  flags: string[] | null;
  status: string;
  notes: string | null;
  added_at: string;
  enriched_at: string | null;
  enrichment_error: string | null;
}

const STATUS_OPTIONS = [
  'discovered',
  'shortlisted',
  'outreached',
  'negotiating',
  'booked',
  'published',
  'paid',
  'archived',
] as const;

type SortKey = 'fit' | 'followers' | 'er' | 'added';

export function InfluencersPage() {
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('fit');
  const [sortDesc, setSortDesc] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['influencers'],
    queryFn: async (): Promise<InfluencerRow[]> => {
      const { data, error } = await marketing
        .from('influencers')
        .select('*')
        .order('added_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as InfluencerRow[];
    },
    staleTime: 30_000,
  });

  const enrich = useMutation({
    mutationFn: async (handle: string) => {
      const { data, error } = await marketing.functions.invoke<{
        ok: boolean;
        influencer?: InfluencerRow;
        error?: string;
      }>('influencer-enrich', { body: { handle } });
      if (error) throw new Error(error.message);
      if (!data?.ok) throw new Error(data?.error ?? 'unknown error');
      return data.influencer!;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['influencers'] }),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await marketing.from('influencers').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['influencers'] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await marketing.from('influencers').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['influencers'] }),
  });

  const filtered = useMemo(() => {
    const all = list.data ?? [];
    const f = filter.trim().toLowerCase();
    const rows = all.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (f) {
        const hay = `${r.ig_handle} ${r.display_name ?? ''} ${(r.themes ?? []).join(' ')}`.toLowerCase();
        if (!hay.includes(f)) return false;
      }
      return true;
    });
    rows.sort((a, b) => {
      const dir = sortDesc ? -1 : 1;
      const get = (r: InfluencerRow) => {
        switch (sortKey) {
          case 'fit':
            return r.brand_fit_score ?? -1;
          case 'followers':
            return r.followers ?? -1;
          case 'er':
            return r.engagement_rate_pct ?? -1;
          case 'added':
            return new Date(r.added_at).getTime();
        }
      };
      return (Number(get(a)) - Number(get(b))) * dir;
    });
    return rows;
  }, [list.data, filter, statusFilter, sortKey, sortDesc]);

  const handles = useMemo(
    () =>
      input
        .split(/[\s,]+/)
        .map((s) => s.replace(/^@/, '').trim().toLowerCase())
        .filter((s) => /^[a-z0-9._]{1,30}$/.test(s)),
    [input],
  );

  const enrichAll = async () => {
    for (const h of handles) {
      try {
        await enrich.mutateAsync(h);
      } catch {
        // mutation surfaces the error in the row's enrichment_error
      }
    }
    setInput('');
  };

  const counts = useMemo(() => {
    const all = list.data ?? [];
    const map: Record<string, number> = { all: all.length };
    for (const s of STATUS_OPTIONS) map[s] = 0;
    for (const r of all) map[r.status] = (map[r.status] ?? 0) + 1;
    return map;
  }, [list.data]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Influencer pipeline</h1>
        <p className="text-sm text-muted-foreground">
          Paste an Instagram handle (or several, one per line). The AI enriches profile + last 12
          posts, classifies themes, and scores Flax brand fit.
        </p>
      </header>

      {/* Add handles manually */}
      <MotionCard delay={0.04}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-500" /> Add by handle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`@chefmeghnasfoodmagic\n@nutbynaturee\n@thatfittyhealthy`}
                className={cn(
                  'min-h-[88px] rounded-md border border-border bg-background p-3 text-sm font-mono',
                  'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
              <div className="flex items-center justify-between">
                <div className="text-2xs text-muted-foreground">
                  {handles.length} valid handle{handles.length === 1 ? '' : 's'} parsed
                  {enrich.isPending && (
                    <span className="ml-2">· enriching @{enrich.variables}…</span>
                  )}
                </div>
                <Button
                  onClick={enrichAll}
                  disabled={handles.length === 0 || enrich.isPending}
                  size="sm"
                >
                  {enrich.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      Enriching…
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-3.5 w-3.5" /> Enrich {handles.length || ''}
                    </>
                  )}
                </Button>
              </div>
              {enrich.isError && (
                <div className="rounded-md border border-rose-500/30 bg-rose-500/10 p-2 text-xs text-rose-600">
                  {(enrich.error as Error).message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </MotionCard>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 sticky top-0 z-10 -mx-4 bg-background/80 px-4 py-2 backdrop-blur-sm md:-mx-6 md:px-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search handle, name, theme…"
            className="w-full rounded-md border border-border bg-background pl-8 pr-2 py-1.5 text-xs"
          />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto">
          <Filter className="h-3 w-3 text-muted-foreground" />
          <StatusChip
            active={statusFilter === 'all'}
            label={`All ${counts.all ?? 0}`}
            onClick={() => setStatusFilter('all')}
          />
          {STATUS_OPTIONS.map((s) => (
            <StatusChip
              key={s}
              active={statusFilter === s}
              label={`${s} ${counts[s] ?? 0}`}
              onClick={() => setStatusFilter(s)}
            />
          ))}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              title="No creators yet"
              description="Paste a handle above to start the pipeline."
            />
          ) : (
            <div className="divide-y divide-border/60">
              <div className="grid grid-cols-[3rem_minmax(0,2fr)_repeat(4,minmax(0,1fr))_2.5rem] items-center gap-3 px-4 py-2 text-2xs uppercase tracking-wide text-muted-foreground">
                <span />
                <span>Creator</span>
                <SortHeader
                  label="Fit"
                  active={sortKey === 'fit'}
                  desc={sortDesc}
                  onClick={() => toggleSort('fit', sortKey, sortDesc, setSortKey, setSortDesc)}
                />
                <SortHeader
                  label="Followers"
                  active={sortKey === 'followers'}
                  desc={sortDesc}
                  onClick={() => toggleSort('followers', sortKey, sortDesc, setSortKey, setSortDesc)}
                />
                <SortHeader
                  label="ER"
                  active={sortKey === 'er'}
                  desc={sortDesc}
                  onClick={() => toggleSort('er', sortKey, sortDesc, setSortKey, setSortDesc)}
                />
                <span>Status</span>
                <span />
              </div>
              {filtered.map((r) => (
                <Row
                  key={r.id}
                  r={r}
                  isExpanded={expanded === r.id}
                  onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                  onStatus={(status) => updateStatus.mutate({ id: r.id, status })}
                  onDelete={() => remove.mutate(r.id)}
                  onReEnrich={() => enrich.mutate(r.ig_handle)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({
  r,
  isExpanded,
  onToggle,
  onStatus,
  onDelete,
  onReEnrich,
}: {
  r: InfluencerRow;
  isExpanded: boolean;
  onToggle: () => void;
  onStatus: (status: string) => void;
  onDelete: () => void;
  onReEnrich: () => void;
}) {
  const fit = r.brand_fit_score ?? 0;
  const fitColor =
    fit >= 80
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
      : fit >= 50
        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
        : fit >= 20
          ? 'bg-slate-500/15 text-slate-700 dark:text-slate-400'
          : 'bg-rose-500/15 text-rose-700 dark:text-rose-400';

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="grid w-full grid-cols-[3rem_minmax(0,2fr)_repeat(4,minmax(0,1fr))_2.5rem] items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/30"
      >
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-border/60 bg-muted">
          {r.profile_picture_url ? (
            <img
              src={imgProxy(r.profile_picture_url)}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : (
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">@{r.ig_handle}</span>
            {r.enrichment_error && (
              <AlertTriangle className="h-3 w-3 shrink-0 text-rose-500" />
            )}
          </div>
          {r.display_name && (
            <div className="truncate text-2xs text-muted-foreground">{r.display_name}</div>
          )}
        </div>
        <div>
          <span
            className={cn(
              'inline-flex min-w-[2.5rem] justify-center rounded-md px-2 py-0.5 text-2xs font-semibold tabular-nums',
              fitColor,
            )}
          >
            {r.brand_fit_score ?? '—'}
          </span>
        </div>
        <span className="text-sm tabular-nums">
          {r.followers != null ? formatNumberCompact(r.followers) : '—'}
        </span>
        <span className="text-sm tabular-nums">
          {r.engagement_rate_pct != null ? `${r.engagement_rate_pct.toFixed(2)}%` : '—'}
        </span>
        <div onClick={(e) => e.stopPropagation()}>
          <StatusSelect value={r.status} onChange={onStatus} />
        </div>
        <span className="flex justify-end text-muted-foreground">
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>

      {isExpanded && (
        <div className="border-t border-border/40 bg-muted/30 p-4 space-y-3">
          {r.enrichment_error && (
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-700 dark:text-rose-400">
              {r.enrichment_error}
            </div>
          )}

          {r.fit_rationale && (
            <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3 text-sm">
              <div className="text-2xs font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                Why this fit score
              </div>
              <p className="mt-1 leading-relaxed">{r.fit_rationale}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="Posts/wk"
              value={r.posts_per_week != null ? r.posts_per_week.toFixed(1) : '—'}
            />
            <Stat
              label="Reel mix"
              value={r.reel_pct != null ? `${Math.round(r.reel_pct)}%` : '—'}
            />
            <Stat
              icon={<Heart className="h-3 w-3" />}
              label="Avg likes"
              value={r.avg_likes != null ? formatNumberCompact(Math.round(r.avg_likes)) : '—'}
            />
            <Stat
              icon={<Users className="h-3 w-3" />}
              label="Total posts"
              value={r.posts_total != null ? formatNumberCompact(r.posts_total) : '—'}
            />
          </div>

          {r.themes && r.themes.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {r.themes.map((t) => (
                <Badge key={t} variant="muted" className="text-2xs">
                  {t}
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {r.est_cost_band && (
              <Badge variant="muted" className="text-2xs">
                Est. cost: {prettyCost(r.est_cost_band)}
              </Badge>
            )}
            {r.flags?.map((f) => (
              <Badge
                key={f}
                variant="muted"
                className="bg-rose-500/15 text-2xs text-rose-700 dark:text-rose-400"
              >
                <AlertTriangle className="mr-1 h-3 w-3" />
                {f.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>

          {r.bio && (
            <p className="text-xs text-muted-foreground whitespace-pre-wrap">{r.bio}</p>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <a
              href={`https://www.instagram.com/${r.ig_handle}/`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              Open IG <ExternalLink className="h-3 w-3" />
            </a>
            {r.website && (
              <a
                href={r.website}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                Website <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <button
              type="button"
              onClick={onReEnrich}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              <RefreshCw className="h-3 w-3" /> Re-enrich
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirm(`Remove @${r.ig_handle}?`)) onDelete();
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-500/15 dark:text-rose-400"
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
            {r.enriched_at && (
              <span className="ml-auto inline-flex items-center gap-1 text-2xs text-muted-foreground">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                Enriched{' '}
                {new Date(r.enriched_at).toLocaleDateString('en-IN', {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (s: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-border bg-background px-2 py-1 text-2xs"
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

function StatusChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-md border px-2 py-1 text-2xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-card text-muted-foreground hover:bg-accent',
      )}
    >
      {label}
    </button>
  );
}

function SortHeader({
  label,
  active,
  desc,
  onClick,
}: {
  label: string;
  active: boolean;
  desc: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 text-2xs uppercase tracking-wide',
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
      {active &&
        (desc ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
    </button>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-md border border-border/40 bg-card p-2">
      <div className="flex items-center gap-1 text-2xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function prettyCost(c: string): string {
  switch (c) {
    case 'free_barter':
      return 'Free / barter';
    case 'inr_5_15k':
      return '₹5-15k';
    case 'inr_15_40k':
      return '₹15-40k';
    case 'inr_40k_plus':
      return '₹40k+';
    default:
      return c;
  }
}

function toggleSort(
  next: SortKey,
  current: SortKey,
  currentDesc: boolean,
  setKey: (k: SortKey) => void,
  setDesc: (d: boolean) => void,
) {
  if (next === current) {
    setDesc(!currentDesc);
  } else {
    setKey(next);
    setDesc(true);
  }
}

